'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();

import { useEffect } from 'react'

import { pixelsEnabled } from '@/config/analytics'
import { clickIdsForServerEvent } from '@/lib/analytics/attribution'
import { firePixelViewContent } from '@/lib/analytics/pixels'

/**
 * Records the ad pixels' `ViewContent` for the page it is mounted on, twice over: once from the
 * browser, once from this site's own server.
 *
 * Both fires share one uuid. The browser half goes to Meta and TikTok directly (`eventID` /
 * `event_id`); the server half is a same-origin POST to `/api/view-content`, which captures
 * `content_viewed` into PostHog with the visitor's IP, user agent and first-party ad cookies, and
 * from there the Meta destination sends the Conversions API twin. Meta deduplicates the pair on
 * the id. The server half is what survives an ad blocker; the browser half is what carries the
 * pixel's own cookies to Meta. Neither waits for PostHog's browser SDK, which loads lazily and
 * would miss a visitor who leaves within two seconds — `keepalive` lets the POST outlive the page.
 *
 * Fires once per mount — on a cold load and on a client-side navigation alike, because a route
 * change mounts a fresh page tree. Deliberately a page-level component rather than a branch in
 * `PixelPageviews`: that hook skips the first render, which is exactly the ad landing this event
 * is for. Neither platform's fire depends on its script having loaded by then:
 * `firePixelViewContent` holds each until the pixel's global is on the page.
 */
export function PixelViewContent({ contentName }: { contentName: string }) {
  useEffect(() => {
    if (!pixelsEnabled) {
      return
    }

    const eventId = newEventId()
    firePixelViewContent(contentName, eventId)

    const { fbclid, ttclid, seenAt } = clickIdsForServerEvent()
    void fetch('/api/view-content', {
      method: 'POST',
      keepalive: true,
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        eventId,
        content: contentName,
        url: window.location.href,
        ...(document.referrer ? { referrer: document.referrer } : {}),
        ...(fbclid ? { fbclid } : {}),
        ...(ttclid ? { ttclid } : {}),
        ...(seenAt ? { seenAt } : {}),
      }),
    }).catch(() => {
      // The browser half has already fired; a lost server twin is not worth surfacing to a visitor.
    })
  }, [contentName])

  return null
}

/**
 * A v4 uuid. `crypto.randomUUID` needs a secure context, which `pnpm dev` on plain http is not, so
 * the fallback builds one from `getRandomValues`.
 */
function newEventId(): string {
  // Typed as optional on purpose: the lib types declare it unconditionally, the runtime does not.
  const { randomUUID } = crypto as Crypto & { randomUUID?: () => string }
  if (randomUUID) {
    return randomUUID.call(crypto)
  }
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
}
